import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { environment } from '@rtls-environment/environment';
import { UserPreferencesService } from '@rtls/core/user/user-preferences.service';
import { ConsignmentNotesBusinessRules } from '@rtls/core/business-rules/consignment-notes-business-rules';
import { AutoRefreshService } from '@rtls/core/data/auto-refresh.service';
import { CacheService } from '@rtls/core/data/cache.service';
import { CompanyService } from '@rtls/core/data/company/company.service';
import { ConsignmentNotesService } from '@rtls/core/data/consignment-notes/consignment-notes.service';
import { CountryService } from '@rtls/core/data/country.service';
import { BusinessService } from '@rtls/core/data/dashboard/business.service';
import { RulesService } from '@rtls/core/data/devs/RulesService';
import { FacetsService } from '@rtls/core/data/filter/facets.service';
import { FilterHelperService } from '@rtls/core/data/filter/filter-helper.service';
import { DriverService } from '@rtls/core/data/human/driver.service';
import { PlanningService } from '@rtls/core/data/planning/planning.service';
import { PoIService } from '@rtls/core/data/poi.service';
import { StepLocationService } from '@rtls/core/data/step-location.service';
import { Step2Service } from '@rtls/core/data/step2.service';
import { TagAttachementService } from '@rtls/core/data/tag/tag-attachement.service';
import { TagService } from '@rtls/core/data/tag/tag.service';
import { ThingAnomaliesService } from '@rtls/core/data/thing/thing-anomalies.service';
import { ThingAvailabilityService } from '@rtls/core/data/thing/thing-availability.service';
import { ThingHistoryService } from '@rtls/core/data/thing/thing-history.service';
import { ThingTypeService } from '@rtls/core/data/thing/thing-type.service';
import { ThingService } from '@rtls/core/data/thing/thing.service';
import { JourneyPlansService } from '@rtls/core/data/transport/journey-plans.service';
import { LocationTypeService } from '@rtls/core/data/location/location-type.service';
import { LocationService } from '@rtls/core/data/location/location.service';
import { DateService } from '@rtls/core/date/date.service';
import { LocaleService } from '@rtls/core/locale/locale.service';
import { BackendRtlsExternalService } from '@rtls/core/models/rtls/impl/rtls-external-service.backend';
import { MockedRtlsExternalService } from '@rtls/core/models/rtls/impl/rtls-external-service.mock';
import { BackendRtlsService } from '@rtls/core/models/rtls/impl/rtls-service.backend';
import { MockedRtlsService } from '@rtls/core/models/rtls/impl/rtls-service.mock';
import { RtlsUserServiceBackend } from '@rtls/core/models/rtls/keycloak/rtls-user-service-backend';
import { RtlsUserService } from '@rtls/core/models/rtls/keycloak/rtls-user-service.model';
import { RtlsExternalService } from '@rtls/core/models/rtls/rtls-external-service.model';
import { RtlsGenericService } from '@rtls/core/models/rtls/rtls-generic-service.model';
import { RtlsService } from '@rtls/core/models/rtls/rtls-service.model';
import { throwIfAlreadyLoaded } from '@rtls/core/module-import-guard';
import { ScriptStoreService } from '@rtls/core/script/script.store.service';
import { IntraRoutingService } from '@rtls/core/util/route/intra-routing.service';
import { StringUtilService } from '@rtls/core/util/string-util.service';
import { ThingAvailabilityPipe } from '@rtls/shared/pipes/thing-pipes/thing-availability.pipe';
import { ThingStateToFilenamePipe } from '@rtls/shared/pipes/thing-pipes/thing-state-to-filename.pipe';
import { SharedModule } from '@rtls/shared/shared.module';
import { MessageService } from 'primeng/api';
import { ThingAvailabilityFacetsService } from './data/thing/thing-availability-facets.service';
import { StopsService } from './data/transport/stops.service';
import { PreferencesStore } from '@rtls/core/preferences/preferences-store';
import { PreferencesService } from '@rtls/core/preferences/preferences.service';
import { ThingLatestActivityToFilenamePipe } from '@rtls/shared/pipes/thing-pipes/thing-latest-activity-to-filename.pipe';
import { RentalContractService } from '@rtls/core/data/thing/rental-contract.service';
import { CabaneService } from '@rtls/core/cabane/cabane.service';
import { WagonSetsDataService } from '@rtls/core/data/transport/wagon-sets-data.service';

@NgModule({
	imports: [SharedModule],
	providers: [
		PreferencesStore,
		PreferencesService,
		AutoRefreshService,
		BusinessService,
		CacheService,
		CompanyService,
		CountryService,
		DateService,
		DatePipe,
		DriverService,
		FacetsService,
		FilterHelperService,
		IntraRoutingService,
		LocaleService,
		MessageService,
		LocationService,
		LocationTypeService,
		PlanningService,
		PoIService,
		RulesService,
		ScriptStoreService,
		Step2Service,
		StepLocationService,
		StopsService,
		StringUtilService,
		TagAttachementService,
		TagService,
		ThingStateToFilenamePipe,
		ThingLatestActivityToFilenamePipe,
		ThingAvailabilityPipe,
		UserPreferencesService,
		ThingHistoryService,
		ThingService,
		ThingTypeService,
		RentalContractService,
		CabaneService,
		ThingAvailabilityService,
		ThingAvailabilityFacetsService,
		JourneyPlansService,
		ThingAnomaliesService,
		ConsignmentNotesBusinessRules,
		WagonSetsDataService,
		{ provide: ConsignmentNotesService, useClass: ConsignmentNotesService },
		{ provide: RtlsUserService, useClass: RtlsUserServiceBackend },
		{
			provide: RtlsService,
			useClass: environment.mock ? MockedRtlsService : BackendRtlsService,
		},
		{
			provide: RtlsExternalService,
			useClass: environment.mock ? MockedRtlsExternalService : BackendRtlsExternalService,
		},
		{
			provide: RtlsGenericService,
			useClass: environment.mock ? MockedRtlsExternalService : BackendRtlsExternalService,
		},
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class CoreModule {
	constructor(
		@Optional()
		@SkipSelf()
		parentModule: CoreModule,
	) {
		throwIfAlreadyLoaded(parentModule, 'CoreModule');
	}
}
